import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import {
  type BizneoConfig,
  type BizneoCredentialsResponse,
  type ConnectBizneoArgs
} from 'src/modules/Settings/Integrations/pages/components/integrations2.0/Bizneo/utils';
import { type IntegrationParamOption } from './common';
import { zeusApiV1 } from 'src/store/api';

type BizneoParamsResponse = {
  companies: IntegrationParamOption[];
  absenceTypes: IntegrationParamOption[];
  customFields: IntegrationParamOption[];
};

const extendedApi = zeusApiV1.injectEndpoints({
  endpoints: build => ({
    saveBizneo: build.mutation<BizneoCredentialsResponse, BizneoConfig & { subsidiary: string }>({
      query: ({ subsidiary, ...body }) => ({
        url: '/hercules/v1/bizneo/config',
        method: 'PATCH',
        body,
        params: { subsidiary }
      }),
      invalidatesTags: ['integration']
    }),
    connectBizneo: build.mutation<BizneoCredentialsResponse, ConnectBizneoArgs>({
      queryFn: async ({ subsidiary, body }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/bizneo/credentials',
            method: 'PUT',
            params: { subsidiary },
            body
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as BizneoCredentialsResponse };
        } catch (error) {
          console.error('Error connecting Bizneo:', error);

          return { error: error as FetchBaseQueryError };
        }
      },
      invalidatesTags: ['integration', 'bizneo']
    }),
    getBizneoParams: build.query<BizneoParamsResponse, { subsidiary: string }>({
      async queryFn({ subsidiary }, _api, _extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/bizneo/company-params',
            params: { subsidiary }
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as BizneoParamsResponse };
        } catch (error) {
          console.error('Error fetching Bizneo params:', error);

          return { error: error as FetchBaseQueryError };
        }
      },
      providesTags: ['bizneo']
    })
  }),
  overrideExisting: false
});

export const { useSaveBizneoMutation, useConnectBizneoMutation, useGetBizneoParamsQuery } = extendedApi;
